import { inject, Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
// eslint-disable-next-line @nx/enforce-module-boundaries
import { WebchatUpgradeCta, WebchatUpgradeCtaState } from '@galaxy/conversation/core';
import { AppKey, AppSettings } from '@galaxy/marketplace-apps';
import { SalesInfoModalComponent } from '@vendasta/business-nav';
import { combineLatest, Observable, of } from 'rxjs';
import { catchError, map, shareReplay, switchMap, take } from 'rxjs/operators';
import { editionIds, productIds } from '../../globals';
import { AccountGroupService } from '../account-group';
import { AppPartnerService } from '../core/app_partner.service';
import { IgniteTrialService } from '../core/ignite-trial.service';
import { Product } from '../core/product';
import { ProductUpgradeService } from '../core/product-upgrade.service';
import { webchatUpgradeCtaState } from './webchat-upgrade-cta-state';

const UPGRADE_SOURCE = 'webchat-settings-cta';

@Injectable({ providedIn: 'root' })
export class WebchatUpgradeCtaService implements WebchatUpgradeCta {
  private readonly accountGroupService = inject(AccountGroupService);
  private readonly appPartnerService = inject(AppPartnerService);
  private readonly productUpgrade = inject(ProductUpgradeService);
  private readonly igniteTrial = inject(IgniteTrialService);
  private readonly dialog = inject(MatDialog);

  readonly state$: Observable<WebchatUpgradeCtaState> = this.accountGroupService.currentAccountGroup$.pipe(
    switchMap((ag) =>
      this.appPartnerService.getAppSettings(productIds.conversationAI, ag.marketId).pipe(
        map((settings) => webchatUpgradeCtaState(settings ?? null)),
        catchError((err: { status?: number }) =>
          of<WebchatUpgradeCtaState>(err?.status === 404 ? webchatUpgradeCtaState(null) : 'viewProduct'),
        ),
      ),
    ),
    shareReplay({ refCount: true, bufferSize: 1 }),
  );

  onClick(state: Exclude<WebchatUpgradeCtaState, 'viewProduct'>): void {
    if (state === 'contactUs') {
      this.dialog.open(SalesInfoModalComponent, { maxWidth: '100vw', maxHeight: '100vh', autoFocus: false });
      return;
    }
    combineLatest([this.igniteTrial.canStartTrial$, this.igniteTrial.pendingTrialConversion$])
      .pipe(
        take(1),
        switchMap(([canStartTrial, pendingConversion]) =>
          canStartTrial && !pendingConversion ? this.productName$() : of(''),
        ),
      )
      .subscribe((productName) => {
        if (productName) {
          this.igniteTrial.startTrialFor({
            appId: productIds.conversationAI,
            editionId: editionIds.conversationAIPremium,
            productName,
          });
          return;
        }
        this.productUpgrade.openUpgradeDialog({ appId: productIds.conversationAI, source: UPGRADE_SOURCE });
      });
  }

  private productName$(): Observable<string> {
    return this.accountGroupService.currentAccountGroup$.pipe(
      take(1),
      switchMap((ag) =>
        combineLatest([
          this.appPartnerService.getMulti([new AppKey({ appId: productIds.conversationAI })], ag.marketId).pipe(
            map((apps) => (apps?.[0] ? (Product.fromV2App(apps[0]).name ?? '') : '')),
            catchError(() => of('')),
          ),
          this.appPartnerService
            .getAppSettings(productIds.conversationAI, ag.marketId)
            .pipe(catchError(() => of<AppSettings | null>(null))),
        ]),
      ),
      map(([defaultName, settings]) =>
        settings?.branding?.enabled && settings.branding.name ? settings.branding.name : defaultName,
      ),
    );
  }
}
